import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, Organization } from '../lib/api';
import { labelFor, ORG_STATUS_LABELS } from '../lib/labels';
import { Building2, Users, Coins, Loader, ChevronRight, RefreshCw, Plus, X, Trash2, Search } from 'lucide-react';

const ORG_LIST_LIMIT = 1000;

const pluralOrganizations = (n: number) => `${n.toLocaleString()} ${n === 1 ? 'organization' : 'organizations'}`;

export const Organizations: React.FC = () => {
  const { token } = useAdminAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingOrgId, setDeletingOrgId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [newOrg, setNewOrg] = useState({
    organizationName: '',
    organizationSlug: '',
    maxUsers: 10,
    // 28 Sep (mig 0702): a starting GRANT into the one balance, plus what the
    // organisation's success fee is agreed for. No plan.
    startingCredits: 1000,
    useCase: '',
    ownerEmail: '',
    ownerFirstName: '',
    ownerLastName: '',
    ownerPassword: ''
  });

  const api = new AdminAPI(token!);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const data = await api.getOrganizations(0, ORG_LIST_LIMIT);
      setOrganizations(data);
      setError('');
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      setError(`Couldn't load organizations (${reason}). Reload the page to try again.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  const handleSyncFromAWS = async () => {
    if (!confirm('This will synchronize all users and organizations from AWS Cognito. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      setError('');
      setSuccess('');
      
      const result = await api.syncFromAWS();
      
      setSuccess(result.message);
      await loadOrganizations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to synchronize from AWS');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteOrganization = async (org: Organization, e: React.MouseEvent) => {
    e.stopPropagation();
    const userCount = org.current_users ?? 0;
    const confirmMsg = `Delete "${org.name}"?\n\n` +
      `This removes ${userCount.toLocaleString()} user sign-in${userCount === 1 ? '' : 's'} (AWS Cognito), all agents, knowledge bases, ` +
      `conversations, and the organization itself. This cannot be undone.\n\n` +
      `Type the organization slug "${org.slug}" below to confirm.`;
    const typed = window.prompt(confirmMsg);
    if (typed !== org.slug) {
      if (typed !== null) {
        setError(`That didn't match "${org.slug}". Nothing was deleted — try again to delete.`);
      }
      return;
    }

    try {
      setDeletingOrgId(org.id);
      setError('');
      setSuccess('');
      const result = await api.deleteOrganization(org.id);
      setSuccess(result.message || `Deleted ${org.name}`);
      await loadOrganizations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete organization');
    } finally {
      setDeletingOrgId(null);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      setError('');
      setSuccess('');
      
      const { useCase, ...org } = newOrg;
      const created = await api.createOrganization(org);
      const newId: string | undefined = created?.organization?.id ?? created?.id;
      if (useCase.trim() && newId) {
        try { await api.setOrgTerms(newId, { use_case: useCase.trim() }); }
        catch (e) { setError(`Organization created, but the use case wasn't saved: ${e instanceof Error ? e.message : String(e)}`); }
      }

      setSuccess('Organization created');
      setShowAddModal(false);
      setNewOrg({
        organizationName: '',
        organizationSlug: '',
        maxUsers: 10,
        startingCredits: 1000,
        useCase: '',
        ownerEmail: '',
        ownerFirstName: '',
        ownerLastName: '',
        ownerPassword: ''
      });
      await loadOrganizations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const normalizedQuery = query.trim().toLowerCase();
  const statusOptions = Array.from(new Set(organizations.map(o => o.status).filter(Boolean))).sort();
  const filteredOrganizations = organizations.filter(org => {
    if (statusFilter !== 'all' && org.status !== statusFilter) return false;
    if (!normalizedQuery) return true;
    return org.name.toLowerCase().includes(normalizedQuery) || org.slug.toLowerCase().includes(normalizedQuery);
  });
  const isFiltered = normalizedQuery !== '' || statusFilter !== 'all';
  const mayBeMore = organizations.length >= ORG_LIST_LIMIT;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3 text-gray-600">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        <span>Loading organizations…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Organizations</h1>
          <p className="text-gray-600 mt-1">Manage all organizations in the platform</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSyncFromAWS}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {syncing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Sync from AWS
              </>
            )}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add organization
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          {success}
        </div>
      )}

      {organizations.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or slug"
              aria-label="Search organizations by name or slug"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>{labelFor(ORG_STATUS_LABELS, status)}</option>
            ))}
          </select>
          <p className="text-sm text-gray-500 sm:ml-2">
            {isFiltered
              ? `Showing ${filteredOrganizations.length.toLocaleString()} of ${pluralOrganizations(organizations.length)}`
              : `Showing ${pluralOrganizations(organizations.length)}`}
            {mayBeMore && ' — there may be more; use search to narrow'}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {filteredOrganizations.map((org) => (
          <div
            key={org.id}
            onClick={() => navigate(`/organizations/${org.id}`)}
            className="bg-white rounded-xl shadow border border-gray-200 p-6 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <h3 className="text-xl font-semibold text-gray-900">{org.name}</h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(org.status)}`}>
                    {labelFor(ORG_STATUS_LABELS, org.status)}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-4 mt-4">
                  <div>
                    <div className="flex items-center space-x-2 text-gray-600 text-sm mb-1">
                      <Users className="w-4 h-4" />
                      <span className="font-medium">Users</span>
                    </div>
                    <p className="text-gray-900 font-semibold">
                      {(org.current_users ?? 0).toLocaleString()} / {(org.max_users ?? 0).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 text-gray-600 text-sm mb-1">
                      <Coins className="w-4 h-4" />
                      <span className="font-medium">Credits left</span>
                    </div>
                    <p className="text-gray-900 font-semibold tabular-nums">
                      {org.total_credits_balance != null ? org.total_credits_balance.toLocaleString() : '—'}
                    </p>
                  </div>

                  <div>
                    <div className="text-gray-600 text-sm mb-1 font-medium">
                      Slug
                    </div>
                    <p className="text-gray-900 text-sm font-mono">
                      {org.slug}
                    </p>
                  </div>

                  <div>
                    <div className="text-gray-600 text-sm mb-1 font-medium">
                      Created
                    </div>
                    <p className="text-gray-900 text-sm">
                      {new Date(org.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={(e) => handleDeleteOrganization(org, e)}
                  disabled={deletingOrgId === org.id}
                  title={`Delete ${org.name}`}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingOrgId === org.id ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                </button>
                <ChevronRight className="w-6 h-6 text-gray-400" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {organizations.length === 0 && !error && (
        <div className="bg-white rounded-xl shadow border border-gray-200 p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No organizations yet</h3>
          <p className="text-gray-600">Organizations will appear here once they're created.</p>
        </div>
      )}

      {organizations.length > 0 && filteredOrganizations.length === 0 && (
        <div className="bg-white rounded-xl shadow border border-gray-200 p-12 text-center">
          <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No organizations match</h3>
          <p className="text-gray-600 mb-4">Try a different name or slug, or clear the filters.</p>
          <button
            onClick={() => { setQuery(''); setStatusFilter('all'); }}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Add Organization Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Add new organization</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization name *
                  </label>
                  <input
                    type="text"
                    value={newOrg.organizationName}
                    onChange={(e) => setNewOrg({ ...newOrg, organizationName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Acme Corporation"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization slug *
                  </label>
                  <input
                    type="text"
                    value={newOrg.organizationSlug}
                    onChange={(e) => setNewOrg({ ...newOrg, organizationSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="acme-corp"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max users
                  </label>
                  <input
                    type="number"
                    value={newOrg.maxUsers}
                    onChange={(e) => setNewOrg({ ...newOrg, maxUsers: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Starting credits
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={newOrg.startingCredits}
                    onChange={(e) => setNewOrg({ ...newOrg, startingCredits: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Use case
                  </label>
                  <input
                    type="text"
                    value={newOrg.useCase}
                    onChange={(e) => setNewOrg({ ...newOrg, useCase: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. motor insurance renewals"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Owner information</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner email *
                    </label>
                    <input
                      type="email"
                      value={newOrg.ownerEmail}
                      onChange={(e) => setNewOrg({ ...newOrg, ownerEmail: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="owner@acme.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner password *
                    </label>
                    <input
                      type="password"
                      value={newOrg.ownerPassword}
                      onChange={(e) => setNewOrg({ ...newOrg, ownerPassword: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Minimum 8 characters"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner first name
                    </label>
                    <input
                      type="text"
                      value={newOrg.ownerFirstName}
                      onChange={(e) => setNewOrg({ ...newOrg, ownerFirstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner last name
                    </label>
                    <input
                      type="text"
                      value={newOrg.ownerLastName}
                      onChange={(e) => setNewOrg({ ...newOrg, ownerLastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrganization}
                disabled={!newOrg.organizationName || !newOrg.organizationSlug || !newOrg.ownerEmail || !newOrg.ownerPassword}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Create organization
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

